// ─── Account / Mailbox entity models ─────────────────────────────────────────
export interface IAccount {
    id: number;
    name: string;
    email: string;
    provider: string | null;
    mailbox_type: MailboxType;
    mailbox_owner_id: number | null;
    connection_status: AccountConnectionStatus;
    sync_status: AccountSyncStatus | null;
    last_sync_at: string | null;
    /** Reason the last sync failed. Only meaningful while `sync_status === 'error'`. */
    last_sync_error: string | null;
    total_threads: number | null;
    unread_count: number | null;
    created_at: string | null;
    default_signature_new: ISignature | null;
    default_signature_reply_forward: ISignature | null;
}

/** Why a mailbox was not queued for sync. A machine vocabulary — never render it raw. */
export const MailboxSyncQueueReason = {
    ALREADY_SYNCING: 'already_syncing',
    QUEUE_FULL: 'queue_full',
    NOT_FOUND: 'not_found',
    NOT_CONNECTED: 'not_connected',
    ACCESS_DENIED: 'access_denied',
} as const;
export type MailboxSyncQueueReason = (typeof MailboxSyncQueueReason)[keyof typeof MailboxSyncQueueReason];

/**
 * One row of `POST /inbox/mailboxes/sync`'s response.
 *
 * The route is ASYNCHRONOUS: `202` means queued, never synced — the mail arrives later
 * over the mailbox SSE stream. A row is returned per requested mailbox even when some are
 * refused, so a mixed batch must be read row by row. `503` (which carries no rows) is the
 * only "nothing at all could be queued" answer.
 */
export interface IMailboxSyncResult {
    mailbox_id: number;
    /** Optional because a pre-async backend omits it — always read via {@link isMailboxSyncQueued}. */
    queued?: boolean;
    reason?: MailboxSyncQueueReason;
    /**
     * Pre-async backend (`200` + `{success, error}`), so a frontend deployed ahead of the
     * backend degrades to "the sync was accepted" instead of toasting every mailbox.
     */
    success?: boolean;
    message?: string;
    error?: string;
}

/** `queued` under the async contract, `success` under the legacy one. */
export function isMailboxSyncQueued(row: IMailboxSyncResult): boolean {
    return row.queued ?? row.success ?? false;
}

/**
 * i18n key for a refused row. An unknown or legacy reason falls back to the generic line,
 * so a reason added on the backend can never render `undefined` in a toast.
 */
export function mailboxSyncReasonKey(row: IMailboxSyncResult): string {
    switch (row.reason) {
        case MailboxSyncQueueReason.ALREADY_SYNCING: return 'smart_inbox.toasts.sync_reason.already_syncing';
        case MailboxSyncQueueReason.QUEUE_FULL: return 'smart_inbox.toasts.sync_reason.queue_full';
        case MailboxSyncQueueReason.NOT_FOUND: return 'smart_inbox.toasts.sync_reason.not_found';
        case MailboxSyncQueueReason.NOT_CONNECTED: return 'smart_inbox.toasts.sync_reason.not_connected';
        case MailboxSyncQueueReason.ACCESS_DENIED: return 'smart_inbox.toasts.sync_reason.access_denied';
        default: return 'smart_inbox.toasts.sync_failed_unknown_reason';
    }
}

export interface IAccountMailbox {
    id?: number;
    name?: string;
    email?: string;
    type?: MailboxType;
}

export const MailboxType = {
    PERSONAL: 'personal',
    TEAM: 'team',
    SHARED: 'shared',
} as const;
export type MailboxType = (typeof MailboxType)[keyof typeof MailboxType];

export const AccountConnectionStatus = {
    PENDING: 'pending',
    CONNECTED: 'connected',
    DISCONNECTED: 'disconnected',
    ERROR: 'error',
} as const;
export type AccountConnectionStatus = (typeof AccountConnectionStatus)[keyof typeof AccountConnectionStatus];

export const AccountSyncStatus = {
    IDLE: 'idle',
    SYNCING: 'syncing',
    ERROR: 'error',
    PAUSED: 'paused',
} as const;
export type AccountSyncStatus = (typeof AccountSyncStatus)[keyof typeof AccountSyncStatus];

/**
 * Mailbox sync state for the views the user is currently looking at, collapsed
 * from the active accounts' `sync_status`.
 *
 * Deliberately distinct from the list's GET loading flag: a mailbox syncing on
 * the server and a `/threads` request in flight are different facts and must not
 * share one banner (they did, and users read the spinner as "the app is working
 * on it" while nothing was happening).
 */
export interface IInboxSyncState {
    /** `syncing` wins over `error`: an errored mailbox that is retrying reads as syncing. */
    status: 'idle' | 'syncing' | 'error';
    /** First non-empty `last_sync_error` among the errored accounts, if any. */
    error: string | null;
    /** Emails of the accounts in the reported state (for the banner's detail line). */
    emails: string[];
}

// ─── Signature models ────────────────────────────────────────────────────────
export interface ISignature {
    id: number;
    name: string;
    body: string;
    mailbox_id: number;
    is_default_new: boolean;
    is_default_reply_forward: boolean;
    active: boolean;
    created_at: string | null;
    updated_at: string | null;
}

export interface ICreateSignaturePayload {
    name: string;
    body: string;
    is_default_new?: boolean;
    is_default_reply_forward?: boolean;
}

export interface IUpdateSignaturePayload {
    name?: string;
    body?: string;
    is_default_new?: boolean;
    is_default_reply_forward?: boolean;
}

// ─── Auto-reply settings (local/dummy until backend support) ─────────────────
export interface IAutoReplySettings {
    enabled: boolean;
}
